import React, { createContext, useContext, useCallback, useState, useRef, useMemo } from 'react';
import usePageVisibility from '../hooks/usePageVisibility';

// Create context
const PageVisibilityContext = createContext({
  isVisible: true,
  wasHidden: () => false,
  refreshNeeded: false,
  setRefreshNeeded: () => {},
  refreshData: () => {},
  registerRefreshCallback: () => () => {},
  lockPageRefresh: () => {},
  unlockPageRefresh: () => {},
  isRefreshLocked: false,
});

// Increase the minimum time between refreshes to 30 minutes (1800000ms)
// This will prevent frequent refreshes when switching tabs
const REFRESH_THROTTLE_MS = 1800000;

// FORCE DISABLE auto-refresh globally
const AUTO_REFRESH_ENABLED = false;

/**
 * Provider component that allows components to subscribe to page visibility changes
 * and manage data refresh when returning to the app
 */
export const PageVisibilityProvider = ({ children }) => {
  const { isVisible, wasHidden } = usePageVisibility();
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const [refreshCallbacks, setRefreshCallbacks] = useState([]);
  const [isRefreshLocked, setIsRefreshLocked] = useState(false);
  const lastRefreshTime = useRef(0);
  const isRefreshing = useRef(false);
  const logPrefix = '[PageVisibilityContext]';
  
  // Functions to lock/unlock page refresh (for forms, editing, etc.)
  const lockPageRefresh = useCallback(() => {
    console.log(`${logPrefix} LOCKING page refresh - user is in editing mode`);
    setIsRefreshLocked(true);
  }, []);
  
  const unlockPageRefresh = useCallback(() => {
    console.log(`${logPrefix} UNLOCKING page refresh - user exited editing mode`);
    setIsRefreshLocked(false);
  }, []);
  
  // Centralized refresh function that calls all registered callbacks
  // Modified to do nothing automatically - only manual refreshes allowed
  const refreshData = useCallback(() => {
    console.log(`${logPrefix} Auto-refresh is permanently disabled`);
    
    // Only proceed for MANUAL refresh button clicks, not visibility changes
    if (refreshCallbacks.length === 0 || isRefreshing.current) {
      console.log(`${logPrefix} No refresh performed: ${refreshCallbacks.length === 0 ? 'No callbacks registered' : 'Already refreshing'}`);
      return;
    }
    
    // For manual refreshes (button clicks), allow execution
    // This will still work when user clicks refresh button
    if (refreshCallbacks.length > 0) {
      isRefreshing.current = true;
      lastRefreshTime.current = Date.now();
      
      try {
        const callbackName = refreshCallbacks[0].name || 'unnamed';
        console.log(`${logPrefix} Executing manual refresh callback: ${callbackName}`);
        refreshCallbacks[0]();
      } catch (error) {
        console.error(`${logPrefix} Error in refresh callback:`, error);
      }
      
      setTimeout(() => {
        isRefreshing.current = false;
      }, 1000);
    }
    
    setRefreshNeeded(false);
  }, [refreshCallbacks]);
  
  // Allow components to register their refresh functions
  const registerRefreshCallback = useCallback((callback) => {
    const callbackName = callback.name || 'unnamed';
    console.log(`${logPrefix} Registering refresh callback: ${callbackName}`);
    
    setRefreshCallbacks(prev => {
      // Check if this callback is already registered to avoid duplicates
      if (prev.some(cb => cb === callback)) {
        console.log(`${logPrefix} Callback ${callbackName} already registered, skipping`);
        return prev;
      }
      
      console.log(`${logPrefix} Added new callback: ${callbackName}, total: ${prev.length + 1}`);
      return [...prev, callback];
    });
    
    // Return a cleanup function to remove this callback
    return () => {
      console.log(`${logPrefix} Unregistering callback: ${callbackName}`);
      setRefreshCallbacks(prev => {
        const remaining = prev.filter(cb => cb !== callback);
        console.log(`${logPrefix} After unregistering ${callbackName}, remaining callbacks: ${remaining.length}`);
        return remaining;
      });
    };
  }, []);
  
  // COMPLETELY DISABLED: When page becomes visible and was hidden, mark that a refresh is needed
  // This is now a no-op to prevent any visibility-based refreshes
  React.useEffect(() => {
    // No-op - we never want to refresh on visibility change
    console.log(`${logPrefix} Visibility change detected but refresh is globally disabled`);
  }, [isVisible, wasHidden, isRefreshLocked]);
  
  // COMPLETELY DISABLED: When refresh is needed, execute it (but throttled)
  // This is now a no-op to prevent any automatic refreshes
  React.useEffect(() => {
    // No-op - we never want automatic refreshes
    if (refreshNeeded) {
      console.log(`${logPrefix} Refresh needed but auto-refresh is permanently disabled`);
      setRefreshNeeded(false); // Clear the flag to avoid stale state
    }
  }, [refreshNeeded]);
  
  // CRITICAL FIX: Use useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isVisible: true, // Always return true to prevent refresh logic
    wasHidden: () => false, // Always return false to prevent refresh logic
    refreshNeeded,
    setRefreshNeeded,
    refreshData,
    registerRefreshCallback,
    lockPageRefresh,
    unlockPageRefresh,
    isRefreshLocked,
    autoRefreshEnabled: false // Always false
  }), [
    refreshNeeded,
    refreshData,
    registerRefreshCallback,
    lockPageRefresh,
    unlockPageRefresh,
    isRefreshLocked
  ]);
  
  return (
    <PageVisibilityContext.Provider value={contextValue}>
      {children}
    </PageVisibilityContext.Provider>
  );
};

// Custom hook to use the page visibility context
export const usePageVisibilityContext = () => useContext(PageVisibilityContext);

export default PageVisibilityContext; 